import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from './lib/auth.js';
import LoginPage from './pages/LoginPage.jsx';
import HomePage from './pages/HomePage.jsx';
import SecurityPage from './pages/SecurityPage.jsx';
import AdminUsersPage from './pages/AdminUsersPage.jsx';
import AdminEmployeesPage from './pages/AdminEmployeesPage.jsx';
import AdminShopsPage from './pages/AdminShopsPage.jsx';
import QuestionsPage from './pages/QuestionsPage.jsx';
import FlashcardPage from './pages/FlashcardPage.jsx';
import FlashcardBrowsePage from './pages/FlashcardBrowsePage.jsx';
import AdminFlashcardPage from './pages/AdminFlashcardPage.jsx';
import AdminFlashcardTrackingPage from './pages/AdminFlashcardTrackingPage.jsx'; // M330
import ExamPage from './pages/ExamPage.jsx';
import AskPage from './pages/AskPage.jsx';
import BookmarksPage from './pages/BookmarksPage.jsx';
import AdminAiSettingsPage from './pages/AdminAiSettingsPage.jsx';
import AdminAiScopePage from './pages/AdminAiScopePage.jsx';
import AdminAiTuningPage from './pages/AdminAiTuningPage.jsx';
// M137 (2026-05-10): admin exam settings + oversight
import AdminExamSettingsPage from './pages/AdminExamSettingsPage.jsx';
import AdminAiPricingPage from './pages/AdminAiPricingPage.jsx';
import AdminCheatLogPage from './pages/AdminCheatLogPage.jsx';
import AdminChatAiPage from './pages/AdminChatAiPage.jsx';
import AdminKnowledgeBasePage from './pages/AdminKnowledgeBasePage.jsx';
import AdminPromptsPage from './pages/AdminPromptsPage.jsx';
import AdminTelegramPage from './pages/AdminTelegramPage.jsx';
import AdminMotivationPage from './pages/AdminMotivationPage.jsx';
import AdminProgressPage from './pages/AdminProgressPage.jsx';
import AdminCostPage from './pages/AdminCostPage.jsx';
import AdminHomePage from './pages/AdminHomePage.jsx';
import RewardsPage from './pages/RewardsPage.jsx';
import AdminRewardsPage from './pages/AdminRewardsPage.jsx';
import AdminRulesModulesPage from './pages/AdminRulesModulesPage.jsx';
import RulesModulesListPage from './pages/RulesModulesListPage.jsx';
import RulesReaderM80Page from './pages/RulesReaderM80Page.jsx';
import AdminRulesM80Page from './pages/AdminRulesM80Page.jsx';
import AdminFlashcardOrderPage from './pages/AdminFlashcardOrderPage.jsx';
import DebugFlashcardPage from './pages/DebugFlashcardPage.jsx';
import AdminAnnouncementPage from './pages/AdminAnnouncementPage.jsx'; // M323
import DialogHost from './components/DialogHost.jsx';
import AnnouncementPopup from './components/AnnouncementPopup.jsx'; // M323

function ProtectedRoute({ children, adminOnly = false, strictAdminOnly = false, perm = null }) {
  const { accessToken, user } = useAuthStore();
  const location = useLocation();

  if (!accessToken) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  // M144a (2026-05-12): strictAdminOnly — CHỈ admin (vd /admin/users, không cho manager).
  if (strictAdminOnly && user?.role !== 'admin') {
    return <Navigate to="/admin" replace />;
  }
  // adminOnly — admin HOẶC manager (kiosk fail). Đây là gate gross-level vào /admin area.
  if (adminOnly && user?.role !== 'admin' && user?.role !== 'manager') {
    return <Navigate to="/" replace />;
  }
  // M144b: route gắn perm — admin pass; manager pass nếu user.permissions.includes(perm); kiosk fail
  if (perm) {
    const isAdmin = user?.role === 'admin';
    const isManagerWithPerm = user?.role === 'manager' && Array.isArray(user?.permissions) && user.permissions.includes(perm);
    if (!isAdmin && !isManagerWithPerm) {
      return <Navigate to="/admin" replace />;
    }
  }
  return children;
}

function PublicOnlyRoute({ children }) {
  const { accessToken, user } = useAuthStore();
  // 2026-04-24: admin → /admin, kiosk → / (HomePage NV)
  if (accessToken) {
    // M144a (2026-05-12): manager cũng vào /admin (giống admin)
    const home = (user?.role === 'admin' || user?.role === 'manager') ? '/admin' : '/';
    return <Navigate to={home} replace />;
  }
  return children;
}

// 2026-04-24: Route / — admin vào sẽ auto-redirect /admin (tránh rối UX).
// M146 (2026-05-12): manager cũng auto-redirect /admin (không phải NV, không có L2).
// Kiosk vẫn thấy HomePage NV bình thường.
function HomeRoute() {
  const user = useAuthStore(s => s.user);
  if (user?.role === 'admin' || user?.role === 'manager') {
    return <Navigate to="/admin" replace />;
  }
  return <HomePage />;
}

export default function App() {
  return (
    <>
    <Routes>
      <Route path="/login" element={<PublicOnlyRoute><LoginPage /></PublicOnlyRoute>} />
      <Route path="/" element={<ProtectedRoute><HomeRoute /></ProtectedRoute>} />
      <Route
        path="/admin"
        element={<ProtectedRoute adminOnly><AdminHomePage /></ProtectedRoute>}
      />
      <Route
        path="/account/security"
        element={<ProtectedRoute><SecurityPage /></ProtectedRoute>}
      />
      <Route
        path="/admin/users"
        element={<ProtectedRoute strictAdminOnly><AdminUsersPage /></ProtectedRoute>}
      />
      <Route
        path="/admin/employees"
        element={<ProtectedRoute adminOnly><AdminEmployeesPage /></ProtectedRoute>}
      />
      <Route
        path="/admin/shops"
        element={<ProtectedRoute adminOnly><AdminShopsPage /></ProtectedRoute>}
      />
      <Route
        path="/admin/questions"
        element={<ProtectedRoute adminOnly><QuestionsPage /></ProtectedRoute>}
      />
      <Route
        path="/flashcards"
        element={<ProtectedRoute><FlashcardPage /></ProtectedRoute>}
      />
      <Route
        path="/flashcards/browse"
        element={<ProtectedRoute><FlashcardBrowsePage /></ProtectedRoute>}
      />
      <Route
        path="/admin/flashcard"
        element={<ProtectedRoute adminOnly><AdminFlashcardPage /></ProtectedRoute>}
      />
      <Route
        path="/exam"
        element={<ProtectedRoute><ExamPage /></ProtectedRoute>}
      />
      <Route
        path="/ask"
        element={<ProtectedRoute><AskPage /></ProtectedRoute>}
      />
      <Route
        path="/bookmarks"
        element={<ProtectedRoute><BookmarksPage /></ProtectedRoute>}
      />
      <Route
        path="/admin/ai"
        element={<ProtectedRoute adminOnly><AdminAiSettingsPage /></ProtectedRoute>}
      />
      <Route
        path="/admin/ai-scope"
        element={<ProtectedRoute adminOnly><AdminAiScopePage /></ProtectedRoute>}
      />
      <Route
        path="/admin/ai-tuning"
        element={<ProtectedRoute adminOnly><AdminAiTuningPage /></ProtectedRoute>}
      />
      <Route
        path="/admin/exam-settings"
        element={<ProtectedRoute adminOnly><AdminExamSettingsPage /></ProtectedRoute>}
      />
      <Route
        path="/admin/ai-pricing"
        element={<ProtectedRoute adminOnly><AdminAiPricingPage /></ProtectedRoute>}
      />
      <Route
        path="/admin/cheat-log"
        element={<ProtectedRoute adminOnly><AdminCheatLogPage /></ProtectedRoute>}
      />
      {/* M170 (2026-05-15): gỡ toàn bộ M9 rules code.
          - /admin/noi-quy (AdminNoiQuyPage) → thay bằng /admin/rules-modules + /admin/rules-m80 (M80).
          - /noi-quy-cu (NoiQuyPage), /noi-quy-cu/exam (NoiQuyExamPage), /noi-quy-m35 (RulesReaderPage) — đã gỡ.
          BE rulesService + rulesExamService + routes/rules.js + routes/rules-exam.js GIỮ cho:
            (1) tests rules-m9.test.js đang pass, (2) rulesEmbedService internal dùng getCurrentVersion.
          Phase 7.7 cleanup sau cùng sẽ gỡ BE nếu không còn caller. */}
      <Route
        path="/admin/chat-ai"
        element={<ProtectedRoute adminOnly><AdminChatAiPage /></ProtectedRoute>}
      />
      <Route
        path="/admin/knowledge-base"
        element={<ProtectedRoute adminOnly><AdminKnowledgeBasePage /></ProtectedRoute>}
      />
      <Route
        path="/admin/prompts"
        element={<ProtectedRoute adminOnly><AdminPromptsPage /></ProtectedRoute>}
      />
      <Route
        path="/admin/telegram"
        element={<ProtectedRoute adminOnly><AdminTelegramPage /></ProtectedRoute>}
      />
      <Route
        path="/admin/motivation"
        element={<ProtectedRoute adminOnly><AdminMotivationPage /></ProtectedRoute>}
      />
      <Route
        path="/admin/progress"
        element={<ProtectedRoute adminOnly><AdminProgressPage /></ProtectedRoute>}
      />
      <Route
        path="/admin/cost"
        element={<ProtectedRoute adminOnly><AdminCostPage /></ProtectedRoute>}
      />
      {/* 2026-04-24: Rewards — NV xem thưởng + admin quản lý chi trả */}
      <Route
        path="/rewards"
        element={<ProtectedRoute><RewardsPage /></ProtectedRoute>}
      />
      <Route
        path="/admin/rewards"
        element={<ProtectedRoute adminOnly><AdminRewardsPage /></ProtectedRoute>}
      />
      {/* 2026-04-24 M35: Học nội quy theo chương — single-page */}
      <Route
        path="/admin/rules-modules"
        element={<ProtectedRoute adminOnly><AdminRulesModulesPage /></ProtectedRoute>}
      />
      {/* M80 (2026-04-26): /noi-quy = list chương; /noi-quy/module/:id = reader 1 chương M80.
          M170 (2026-05-15): gỡ /noi-quy-m35 (RulesReaderPage admin debug) — không link nào dùng. */}
      <Route
        path="/noi-quy"
        element={<ProtectedRoute><RulesModulesListPage /></ProtectedRoute>}
      />
      <Route
        path="/noi-quy/module/:moduleId"
        element={<ProtectedRoute><RulesReaderM80Page /></ProtectedRoute>}
      />
      <Route
        path="/admin/rules-m80"
        element={<ProtectedRoute adminOnly><AdminRulesM80Page /></ProtectedRoute>}
      />
      <Route
        path="/admin/flashcard-order"
        element={<ProtectedRoute adminOnly><AdminFlashcardOrderPage /></ProtectedRoute>}
      />
      <Route
        path="/admin/flashcard-tracking"
        element={<ProtectedRoute adminOnly><AdminFlashcardTrackingPage /></ProtectedRoute>}
      />
      <Route
        path="/debug-flashcard"
        element={<ProtectedRoute><DebugFlashcardPage /></ProtectedRoute>}
      />
      <Route
        path="/admin/announcement"
        element={<ProtectedRoute adminOnly><AdminAnnouncementPage /></ProtectedRoute>}
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    {/* 2026-04-24: Custom dialog system thay native confirm/alert/prompt */}
    <DialogHost />
    {/* M323 (2026-05-30): Popup thông báo cho NV khi đăng nhập */}
    <AnnouncementPopup />
    </>
  );
}
